import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Collapse } from 'ui';
import { fetchMisses, fetchSuggestedOpportunitiesAndMisses } from '../actions';
import { SingleResolveModal, BulkResolveModal } from '../ResolveModal';
import BotTabTableContainer from '../BotTabTableContainer';
import {
  GECKO_CONSTS,
  GeckoTable,
  GeckoPagination,
  GeckoModalService,
  GeckoToastService,
  GeckoBtn,
  GeckoAlert,
  Tooltip,
  OverlayTrigger,
} from 'ui';
import { renderError } from 'services/gecko-error';
import GeckoRequest from 'services/gecko-request';
import GeckoEvents from 'services/gecko-events';
import GeckoDate from 'services/gecko-date';
import GeckoLocation from 'services/gecko-location';
import ProgressRing from 'components/ProgressRing';
import FilterBuilder from 'components/BuildableFilter/FilterBuilder';
import QuickSearch from 'components/BuildableFilter/QuickSearch';
import AnswerSelect from 'components/AnswerSelect';
import BotTagSelect from 'components/BotTagSelect';
import { getFalsePositiveFilter } from 'utilities/botTrainingFilters';
import TagManager from 'react-gtm-module';
import SuggestedOpportunities from './SuggestedOpportunities';
import TrainTableFilters from './TrainTableQuickFilters';
import GeckoFeatures from 'services/gecko-features';
import { CONSTS } from 'constants/index.js';
import './index.scss';

const defaultSortMap = {
  utterance: 'ASC',
  missed_reason: 'ASC',
  confidence: 'DESC',
  createdAt: 'ASC',
  message_ids: 'DESC',
};

class Train extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fetching: false,
      minimumConfidence: 0.65,
      showFilters: false,
      trainings: [],
      selectedMissIds: [],
    };
  }

  componentDidMount() {
    GeckoEvents.subscribe('miss:created', this.handleNewMiss.bind(this));
    GeckoEvents.subscribe('miss:updated', this.handleUpdatedMiss.bind(this));
    const filters = this.props.filters.filter((filter) => {
      return filter.key !== 'closest_response';
    });
    fetchSuggestedOpportunitiesAndMisses({
      page: this.props.page ? this.props.page : 1,
      order: defaultSortMap['confidence'],
      orderBy: 'confidence',
      filters,
      botId: this.props.botId,
    });
    const dataLayer = {
      event: 'optimize.activate',
    };
    TagManager.dataLayer({ dataLayer });
  }

  componentWillUnmount() {
    GeckoEvents.unsubscribe('miss:created', this.handleNewMiss.bind(this));
    GeckoEvents.unsubscribe('miss:updated', this.handleUpdatedMiss.bind(this));
    this.props.setSelectedOpportunity(null);
  }

  componentWillReceiveProps(newProps) {
    if (
      newProps.page > 1 &&
      newProps.misses.length === 0 &&
      !this.state.fetching
    ) {
      this.setState({ fetching: true }, () => {
        fetchMisses({
          page: newProps.page - 1,
          order: newProps.order,
          orderBy: newProps.orderBy,
          filters: newProps.filters,
          botId: newProps.botId,
        }).then(() => {
          this.setState({ fetching: false });
        });
      });
    }

    if (JSON.stringify(this.props.misses) !== JSON.stringify(newProps.misses)) {
      this.setState({ selectedMissIds: [] });
    }
  }

  handleNewMiss() {
    fetchSuggestedOpportunitiesAndMisses({
      page: this.props.page,
      orderBy: this.props.orderBy,
      order: this.props.order,
      filters: this.props.filters,
      botId: this.props.botId,
    });
  }

  handleUpdatedMiss() {
    fetchSuggestedOpportunitiesAndMisses({
      page: this.props.page,
      orderBy: this.props.orderBy,
      order: this.props.order,
      filters: this.props.filters,
      botId: this.props.botId,
    });
  }

  cols = [
    {
      key: 'message_ids',
      align: 'center',
      type: GECKO_CONSTS.TABLE_CELL_CUSTOM,
      render: (message_ids) => {
        const onClick = (event) => {
          event.stopPropagation();
          return GeckoRequest.get(
            `/message/${message_ids[message_ids.length - 1]}`
          )
            .then((message) => {
              const contactId = message.recipient_id
                ? message.recipient_id
                : message.sender_id;
              new GeckoModalService().close();
              return GeckoLocation.goTo(
                `/conversations/${message.conversation_id}`,
                {
                  message: message_ids[message_ids.length - 1],
                  filter: CONSTS.FILTER_CONTACT,
                  contact: contactId,
                }
              );
            })
            .catch(renderError);
        };
        const key = `training-row-${message_ids[message_ids.length - 1]}`;
        return (
          <div className="Train-conversation-link" onClick={onClick}>
            <OverlayTrigger
              placement="auto"
              overlay={<Tooltip id={key}>Open Conversation</Tooltip>}
            >
              <i className="far fa-comments" id={key}></i>
            </OverlayTrigger>
          </div>
        );
      },
    },
    {
      name: 'Message',
      key: 'utterance',
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_TEXT,
    },
    {
      name: 'Confidence',
      key: 'confidence',
      align: 'center',
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_CUSTOM,
      render: (perc) => {
        return (
          <ProgressRing
            radius="21"
            stroke="3"
            progress={Math.round(perc * 100)}
          />
        );
      },
    },
    {
      name: 'Closest Match',
      key: 'closest_response_name',
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_TEXT,
    },
    {
      name: 'Occurrences',
      key: 'message_ids',
      align: 'center',
      render: (message_ids) => {
        return message_ids.length;
      },
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_CUSTOM,
    },
    {
      name: 'Type',
      key: 'missed_reason',
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_TEXT,
      render: (status) => {
        const map = {
          no_match: 'Very low confidence',
          low_confidence: 'Low confidence',
          user_complaint: 'User complaint',
          usefull: 'Marked useful',
          fallback_handled: 'Fallback bot handled',
        };
        return map[status] || status;
      },
    },
    {
      name: 'Date Received',
      key: 'createdAt',
      sortable: true,
      type: GECKO_CONSTS.TABLE_CELL_TYPE_DATE,
      format: new GeckoDate().FORMATS.HIPSTER_SHORT_TIME,
      render: (date) => new Date(date),
    },
    {
      name: 'Resolve',
      key: 'resolve',
      align: 'center',
      type: GECKO_CONSTS.TABLE_CELL_CUSTOM,
      render: (_value, miss) => {
        const dismissMiss = (event) => {
          event.stopPropagation();
          return GeckoRequest.post(`/bots/${this.props.botId}/misses/ignore`, {
            miss_id: miss.id,
          })
            .then(() => {
              const dataLayer = {
                event: 'train_dismiss_answer',
                botResolveFiltersFalsePositive: this.props.isFalsePositive,
                botResolveBotId: this.props.botId,
                botResolveMissId: miss.id,
              };
              TagManager.dataLayer({ dataLayer });
              new GeckoToastService().success('Miss has been ignored');
              fetchMisses({
                page: this.props.page,
                orderBy: this.props.orderBy,
                order: this.props.order,
                botId: this.props.botId,
              });
            })
            .catch((err) => {
              new GeckoToastService().error(err);
            });
        };

        const assignMiss = (miss_id, closest_response) => {
          if (!closest_response) {
            return;
          }
          GeckoRequest.post(`/bots/${this.props.botId}/misses/resolve`, {
            miss_id: miss_id,
            response_id: closest_response,
          })
            .then(() => {
              const dataLayer = {
                event: 'train_resolve_answer',
                botResolveFiltersFalsePositive: this.props.isFalsePositive,
                botResolveBotId: this.props.botId,
                botResolveMissId: miss_id,
                botResolveResponseId: closest_response,
              };
              TagManager.dataLayer({ dataLayer });
              new GeckoToastService().success('Miss resolved');
            })
            .catch(renderError);
        };

        const openResolveModal = () => {
          GeckoRequest.get(`/message/${miss.message_ids[0]}`)
            .then((message) => {
              return new GeckoModalService().open(() => {
                return (
                  <SingleResolveModal
                    botId={this.props.botId}
                    message={message}
                    miss_id={miss.id}
                  />
                );
              });
            })
            .then(() => {
              fetchMisses({
                page: this.props.page,
                orderBy: this.props.orderBy,
                order: this.props.order,
                botId: this.props.botId,
              });
            });
        };

        const onMissClick = (event) => {
          event.stopPropagation();
          if (
            miss.confidence < this.state.minimumConfidence ||
            !miss.closest_response_name
          ) {
            return openResolveModal();
          }
          return assignMiss(miss.id, miss.closest_response);
        };

        const renderId = (type) => {
          if (!miss && !type && !this.props.misses) return `miss-${type}`;
          return `miss-${this.props.misses.indexOf(miss)}-${type}`;
        };
        // ANCHOR - Remove commented code
        return (
          <div className="Train-resolve-column">
            <OverlayTrigger
              placement="auto"
              overlay={
                <Tooltip id={renderId('resolve')}>
                  {miss.confidence < this.state.minimumConfidence ||
                  !miss.closest_response_name
                    ? 'Choose an answer to resolve this miss'
                    : `Resolve answer with closest match - ${miss.closest_response_name}`}
                </Tooltip>
              }
            >
              <i
                className={`far fa-check ${
                  miss.confidence < this.state.minimumConfidence ||
                  !miss.closest_response_name
                    ? 'low-confidence-icon'
                    : ''
                }`}
                id={renderId('resolve')}
                onClick={onMissClick}
              ></i>
            </OverlayTrigger>
            <OverlayTrigger
              placement="auto"
              overlay={
                <Tooltip id={renderId('dismiss')}>Ignore this miss</Tooltip>
              }
            >
              <i
                className="far fa-times"
                id={renderId('dismiss')}
                onClick={dismissMiss}
              ></i>
            </OverlayTrigger>
          </div>
        );
      },
    },
  ];

  rowHandler(row) {
    GeckoRequest.get(`/message/${row.message_ids[0]}`).then((message) => {
      return new GeckoModalService()
        .open(() => {
          return (
            <SingleResolveModal
              message={message}
              botId={this.props.botId}
              miss_id={row.id}
              miss_utterance={row.utterance}
              response_id={row.closest_response}
              miss={[row]}
            />
          );
        })
        .then(() => {
          fetchMisses({
            page: this.props.page,
            orderBy: this.props.orderBy,
            order: this.props.order,
            botId: this.props.botId,
          });
        });
    });
  }

  startTraining() {
    GeckoRequest.post(`/bots/${this.props.botId}/trainings/start`).then(() => {
      new GeckoToastService().success('Training started');
      GeckoRequest.get(`/bots/${this.props.botId}/trainings`).then(
        (trainings) => {
          this.setState({ trainings });
        }
      );
    });
  }

  updateSelectedOpportunity(filters) {
    const filtersHasOpportunityType = filters.find(
      (filter) =>
        filter.key === 'closest_response' &&
        filter.operator === 'custom:response id is'
    );
    if (!filtersHasOpportunityType) {
      this.props.setSelectedOpportunity(null);
      return;
    }
    const closest_response = this.props.selectedOpportunity?.closest_response;
    const { value } = filtersHasOpportunityType;
    if (closest_response === value) return;
    const suggestedOpportunityContainingFilter =
      this.props.suggestedOpportunities
        .slice(0, 3)
        .find((opportunity) => opportunity.closest_response === value);
    if (suggestedOpportunityContainingFilter) {
      const index = this.props.suggestedOpportunities.indexOf(
        suggestedOpportunityContainingFilter
      );
      const { closest_response } = suggestedOpportunityContainingFilter;
      this.props.setSelectedOpportunity({ index, closest_response });
    } else {
      this.props.setSelectedOpportunity(null);
    }
  }

  handleFilterChange(filters) {
    const id = Date.now();
    let showFilters = this.state.showFilters;
    if (this.state.showFilters === true && filters.length === 0) {
      showFilters = false;
    }
    this.setState({ fetchFilters: id, showFilters });
    setTimeout(() => {
      if (this.state.fetchFilters === id) {
        this.updateSelectedOpportunity(filters);
        this.setState({ fetchFilters: null }, () => {
          fetchMisses({ botId: this.props.botId, filters, page: 1 });
        });
      }
    }, 400);
  }

  handleSearch(search) {
    return fetchMisses({
      page: 1,
      botId: this.props.botId,
      orderBy: this.props.orderBy,
      order: this.props.order,
      search,
      filters: this.props.filters,
    });
  }

  handleQuickFilterChange(filters) {
    const id = Date.now();
    const showFilters = filters.length !== 0;
    this.setState({ fetchFilters: id, showFilters });
    setTimeout(() => {
      if (this.state.fetchFilters === id) {
        this.setState({ fetchFilters: null }, () => {
          fetchMisses({ botId: this.props.botId, filters, page: 1 });
        });
      }
    }, 100);
  }

  handleSelectOpportunity = (index, closest_response) => {
    const isClosestMatchFilter = (filter) => {
      return (
        filter.key === 'closest_response' &&
        filter.operator === 'custom:response id is'
      );
    };
    const filtersWithNoOpportunity = this.props.filters.filter(
      (filter) => !isClosestMatchFilter(filter)
    );
    const isAlreadySelected =
      this.props.selectedOpportunity?.closest_response === closest_response;
    const updatedFilters = isAlreadySelected
      ? filtersWithNoOpportunity
      : [
          ...filtersWithNoOpportunity,
          {
            key: 'closest_response',
            operator: 'custom:response id is',
            value: closest_response,
          },
        ];
    const updatedSelectedOpportunity = isAlreadySelected
      ? null
      : { index, closest_response };
    this.props.setSelectedOpportunity(updatedSelectedOpportunity);
    fetchMisses({
      page: this.props.page,
      order: this.props.order,
      orderBy: this.props.orderBy,
      botId: this.props.botId,
      filters: updatedFilters,
    });
  };

  toggleFilters() {
    this.setState({ showFilters: !this.state.showFilters });
  }

  getMassActionOptions() {
    if (GeckoFeatures.can('mass-action-resolve') !== true) {
      return undefined;
    }

    const isRowSelected = (miss) => {
      return this.state.selectedMissIds.includes(miss.id);
    };

    const onRowSelected = (miss) => {
      const currentlySelected = this.state.selectedMissIds;
      if (isRowSelected(miss) === true) {
        const newSelectedMissIds = currentlySelected.filter(
          (missId) => missId !== miss.id
        );
        return this.setState({ selectedMissIds: newSelectedMissIds });
      }
      return this.setState({
        selectedMissIds: [...currentlySelected, miss.id],
      });
    };

    const onHeaderSelected = (misses) => {
      const areAllSelected = misses.every(isRowSelected);
      if (areAllSelected) {
        return this.setState({ selectedMissIds: [] });
      }
      const newSelectedMissIds = misses.map((miss) => miss.id);
      return this.setState({ selectedMissIds: newSelectedMissIds });
    };

    return {
      isRowSelected,
      onRowSelected,
      onHeaderSelected,
    };
  }

  renderMassActionButton() {
    if (this.state.selectedMissIds.length === 0) {
      return null;
    }

    const handler = async () => {
      const firstMiss = this.props.misses.find(
        (miss) => miss.id === this.state.selectedMissIds[0]
      );
      const message = await GeckoRequest.get(
        `/message/${firstMiss.message_ids[0]}`
      );
      await new GeckoModalService().open(() => {
        return (
          <BulkResolveModal
            botId={this.props.botId}
            message={message}
            missIds={this.state.selectedMissIds}
          />
        );
      });
      return fetchMisses({
        page: this.props.page,
        orderBy: this.props.orderBy,
        order: this.props.order,
        botId: this.props.botId,
      });
    };

    return (
      <GeckoBtn
        name={`Mass Resolve (${this.state.selectedMissIds.length})`}
        handler={handler}
        className="Train-mass-resolve-button"
      />
    );
  }

  render() {
    const pagination = {
      changeOrder: (sortKey) => {
        if (this.props.orderBy === sortKey) {
          fetchMisses({
            page: this.props.page,
            botId: this.props.botId,
            orderBy: sortKey,
            order: this.props.order === 'ASC' ? 'DESC' : 'ASC',
          });
        } else {
          fetchMisses({
            page: this.props.page,
            botId: this.props.botId,
            orderBy: sortKey,
            order: defaultSortMap[sortKey],
          });
        }
      },
      changePage: (page) => fetchMisses({ botId: this.props.botId, page }),
      current_page: this.props.page,
      order_by: this.props.orderBy,
      sort: this.props.order,
      total_items: this.props.total,
      total_pages: Math.ceil(this.props.total / 20),
    };

    const fields = [
      {
        key: 'missed_reason',
        type: 'choice',
        label: 'Type',
        choices: [
          {
            label: 'Very Low Confidence',
            value: 'no_match',
          },
          {
            label: 'Low Confidence',
            value: 'low_confidence',
          },
          {
            label: 'User Complaint',
            value: 'user_complaint',
          },
          {
            label: 'Fallback Bot Handled',
            value: 'fallback_handled',
          },
        ],
      },
      {
        key: 'createdAt',
        type: 'date',
        label: 'Recieved at',
      },
      {
        key: 'utterance',
        type: 'text',
        label: 'Message',
      },
      {
        key: 'confidence',
        type: 'number',
        label: 'Confidence',
      },
      {
        key: 'closest_response',
        type: 'custom',
        label: 'Closest Match',
        operators: [
          {
            label: 'is',
            value: 'custom:response id is',
            field: <AnswerSelect botId={this.props.botId} />,
          },
          {
            label: 'is not',
            value: 'custom:response id is not',
            field: <AnswerSelect botId={this.props.botId} />,
          },
          {
            label: 'contains',
            value: 'text:contains',
          },
          {
            label: 'not contains',
            value: 'text:not contains',
          },
          {
            label: 'has tag',
            value: 'custom:tag is',
            field: <BotTagSelect multi={true} />,
          },
          {
            label: "doesn't have tag",
            value: 'custom:tag is not',
            field: <BotTagSelect multi={true} />,
          },
        ],
      },
    ];

    const headerChild = (
      <div
        style={{
          paddingLeft: '10px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {this.renderMassActionButton()}
        <TrainTableFilters
          botId={this.props.botId}
          filters={this.props.filters}
          onChange={this.handleQuickFilterChange.bind(this)}
        />
        <QuickSearch
          showExpandButton={true}
          expanded={this.state.showFilters}
          onSearch={this.handleSearch.bind(this)}
          onToggle={this.toggleFilters.bind(this)}
          filterButtonName=""
          select
        />
      </div>
    );

    const hydratedSuggestedOpportunities = (() => {
      const { suggestedOpportunities, selectedOpportunity } = this.props;
      if (!selectedOpportunity) {
        return suggestedOpportunities.slice(0, 3);
      }

      const { index, closest_response } = selectedOpportunity;
      const orderedSuggestedOpportunities = [...suggestedOpportunities].filter(
        (opportunity) => opportunity.closest_response !== closest_response
      );
      orderedSuggestedOpportunities.splice(
        index,
        0,
        suggestedOpportunities.find(
          (opportunity) => opportunity.closest_response === closest_response
        )
      );
      return orderedSuggestedOpportunities.slice(0, 3);
    })();

    return (
      <div className="Train">
        {!!hydratedSuggestedOpportunities.length && (
          <SuggestedOpportunities
            handleSelectOpportunity={this.handleSelectOpportunity}
            opportunities={hydratedSuggestedOpportunities}
            selectedOpportunity={this.props.selectedOpportunity}
          />
        )}
        <BotTabTableContainer
          title="Training Opportunities"
          noCreate={true}
          headerChild={headerChild}
        >
          <Collapse in={this.state.showFilters}>
            <div>
              {this.state.showFilters ? (
                <FilterBuilder
                  filters={this.props.filters}
                  fields={fields}
                  onChange={this.handleFilterChange.bind(this)}
                />
              ) : null}
            </div>
          </Collapse>
          {this.props.misses && this.props.misses.length ? (
            <div className="gecko-card-body">
              <GeckoTable
                cols={this.cols}
                rows={this.props.misses}
                pagination={pagination}
                rowHandler={this.rowHandler.bind(this)}
                massAction={this.getMassActionOptions()}
              />
              <GeckoPagination pagination={pagination} />
            </div>
          ) : (
            <div className="BotTabTableContainer-table-body gecko-card-body">
              <GeckoAlert
                type={
                  this.props.filters?.length
                    ? GECKO_CONSTS.ALERT_TYPE_WARNING
                    : GECKO_CONSTS.ALERT_TYPE_SUCCESS
                }
              >
                {this.props.filters?.length
                  ? `No training opportunities match your current filter`
                  : `You have no new training opportunities. Good job!`}
              </GeckoAlert>
            </div>
          )}
        </BotTabTableContainer>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    page: state.botsRoute.misses.page,
    orderBy: state.botsRoute.misses.orderBy,
    order: state.botsRoute.misses.order,
    total: state.botsRoute.misses.total,
    misses: state.botsRoute.misses.ids.map(
      (id) => state.botsRoute.missesById[id]
    ),
    filters: state.botsRoute.misses.filters,
    suggestedOpportunities: state.botsRoute.suggestedOpportunities,
    selectedOpportunity: state.botsRoute.selectedOpportunity,
    isFalsePositive: getFalsePositiveFilter(state.botsRoute.misses.filters),
  };
};

const mapDispatchToProps = (dispatch) => ({
  setSelectedOpportunity: (selectedOpportunity) =>
    dispatch({
      type: 'SET_SELECTED_OPPORTUNITY',
      payload: selectedOpportunity,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Train);
